import { isArray, isDefined, isObject } from 'ionic-angular/util/util';
import { Observable } from 'rxjs/Observable';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { of } from 'rxjs/observable/of';
import { _throw } from 'rxjs/observable/throw';
import { ApiRequestOptions } from '../../../core/services/api/request-options';
import { ApiObjectFacade } from '../api-object/api-object.facade';
import { AccountObjectApiFacade } from './account-object.api-facade';
import { AccountObject } from './account-object.model';
import { AccountObjectStoreFacade } from './account-object.store-facade';

export abstract class AccountObjectFacade<T extends AccountObject,
    A extends AccountObjectApiFacade<T>,
    S extends AccountObjectStoreFacade<T>>
    extends ApiObjectFacade<T, A, S> {

    /**
     * This method syncs all items for this entity for either the account
     * that was specified in the accountId argument, or the active account if
     * the accountId argument is undefined.
     *
     * If neither the accountId nor an active is defined the method will do
     * nothing.
     *
     * @param accountId {string}
     * @param options {ApiRequestOptions}
     * @return {Observable<any>}
     */
    syncAll(options?: ApiRequestOptions, accountId?: string): Observable<any> {
        return (
            accountId ?
                of(accountId) :
                this.appFacade.objects.account.store.getActive()
                    .take(1)
                    .map(account => account && account.id)
        )
            .concatMap(id =>
                id ? this.syncAllByAccount(id, options) : of()
            )
            .catch(error => this.handleSyncError(error));
    }

    /**
     * This method synchronises an account object by downloading the object from
     * the API and replacing the local copy if it is fresher
     */
    syncOne(accountId: string, id: string, options?: ApiRequestOptions): Observable<any> {
        return this.api.get(accountId, id, options)
            .catch(error => {
                if (error.status === 404) {
                    this.store.get(id)
                        .take(1)
                        .do(item => {
                            this.store.remove(item, {persistable: true, restful: false});
                        })
                        .subscribe(item => undefined);
                    return of(undefined);
                }
                return _throw(error);
            })
            .map(object => object && this.setAccountId(object, accountId))
            .do(object => object && this.insertIfFresher(object));
    }

    onAfterReceiveApiObjects(objects: Array<T>): Observable<Array<T>> {
        return of(objects);
    }

    onAfterReceiveLocalObjects(objects: Array<T>): Observable<Array<T>> {
        return of(objects);
    }

    protected syncAllByAccount(accountId: string, options?: ApiRequestOptions): Observable<any> {
        return forkJoin(this.getApiAllByAccount(accountId, options).take(1), this.getLocalAllByAccount(accountId).take(1))
            .concatMap(([remote, local]) => {
                return this.onAfterReceiveApiObjects(remote)
                    .map(_remote => {
                        return [_remote, local];
                    });
            })
            .concatMap(([remote, local]) => {
                return this.onAfterReceiveLocalObjects(local)
                    .map(_local => {
                        return [remote, _local];
                    });
            })
            .do(args => {
                let remote: Array<T>;
                let local: Array<T>;
                [remote, local] = args;
                // console.log("SYNC %s", this.getEntity(), remote, local);
                remote.map(object => this.setAccountId(object, accountId));
                this.updateStore(remote, local, options);
            });
    }

    protected getApiAllByAccount(accountId, options?: ApiRequestOptions): Observable<Array<T>> {
        return this.api.getAll(accountId, options);
    }

    protected getLocalAllByAccount(accountId: string): Observable<Array<T>> {
        return this.store.getAllByAccount(accountId);
    }

    protected setAccountId(object: T, accountId: string): T {
        object.account = accountId;
        return this.setAccountIds(object, accountId);
    }

    protected setAccountIds(object, accountId: string): any {

        Object.keys(object)
            .forEach(property => {

                if (isObject(object[property]) && isDefined(object[property].id)) {
                    object[property].account = accountId;
                } else if (isArray(object[property]) && object[property].length > 0 && isDefined(object[property][0].id)) {
                    object[property] = object[property].map(inlinedObject => ({
                        ...inlinedObject,
                        account: accountId
                    }));
                }
            });
        return object;
    }
}



// WEBPACK FOOTER //
// ./src/vas/business-objects/account-object/account-object.facade.ts